import React from 'react';
import PropTypes from 'prop-types';

import {
  SR_APPLICATION_ASSEMBLY_LINE_STATUS_IN_PROGRESS,
  SR_APPLICATION_ASSEMBLY_LINE_STATUS_QUALIFIED,
} from 'common/applicationAssemblyLineConstants';

import SrBox from 'client/components/SrBox';
import SrFlex from 'client/components/SrFlex';
import SrText from 'client/components/SrText';
import SrErrorMessage, {
  SR_ERROR_MESSAGE_THEME_ORANGE,
} from 'client/components/SrErrorMessage/SrErrorMessage';
import SrInfoTile, {
  SR_INFO_TILE_THEME_ADMIN_GREY,
} from 'client/components/SrInfoTile/SrInfoTile';

const SrApplicationAssemblyLineStepLeaseTerm = ({
  assemblyLine,
  householdApplication,
}) => {
  const status = SrApplicationAssemblyLineStepLeaseTerm.getStatus({
    householdApplication,
    assemblyLine,
  });

  const minLeaseTerm = assemblyLine.listing.minLeaseMonths;
  const maxLeaseTerm = assemblyLine.listing.maxLeaseMonths;

  const dateRangeDom = (
    <SrBox display='inlineBlock'>
      <SrFlex
        border='1px solid #EAEBEC'
        borderRadius='8px'
        padding='16px'
      >
        <SrInfoTile
          theme={SR_INFO_TILE_THEME_ADMIN_GREY}
          label='Applicant Lease Term'
          value={householdApplication.leaseTerm
            ? `${householdApplication.leaseTerm} Months`
            : '--'
          }
          valueColor={status === SR_APPLICATION_ASSEMBLY_LINE_STATUS_QUALIFIED
            ? '#00A238'
            : '#E7711B'
          }
        />
        <SrBox height='35px' width='1px' backgroundColor='#EAEBEC' margin='0 24px' />
        <SrInfoTile
          theme={SR_INFO_TILE_THEME_ADMIN_GREY}
          label='Required Lease Term'
          value={minLeaseTerm && maxLeaseTerm
            ? `${minLeaseTerm} - ${maxLeaseTerm} Months`
            : minLeaseTerm
              ? `${minLeaseTerm} Months`
              : maxLeaseTerm
                ? `${maxLeaseTerm} Months`
                : '--'
          }
        />
      </SrFlex>
    </SrBox>
  );

  const informationRequiredDom = (
    <SrBox marginTop='22px'>
      <SrText
        color='orange300'
        font='greycliff'
        weight='700'
        size='18'
        lineHeight='24px'
        display='block'
      >
        Information Required
      </SrText>
      <SrBox marginTop='8px' maxWidth='468px'>
        <SrErrorMessage theme={SR_ERROR_MESSAGE_THEME_ORANGE}>
          Applicant Lease Term
          <br />
          Please ask the applicant to change lease term to a duration that meets the required lease term.
        </SrErrorMessage>
        <SrBox marginTop='16px' />
        <SrText
          color='orange300'
          font='greycliff'
          weight='600'
          size='16'
          lineHeight='24px'
        >
          Once you've asked the applicant for these changes move on to the next applicant in this household.
        </SrText>
      </SrBox>
    </SrBox>
  );

  return (
    <SrBox minWidth='300px'>
      {dateRangeDom}
      {status === SR_APPLICATION_ASSEMBLY_LINE_STATUS_IN_PROGRESS && informationRequiredDom}
    </SrBox>
  );
};

SrApplicationAssemblyLineStepLeaseTerm.propTypes = {
  assemblyLine: PropTypes.object.isRequired,
  householdApplication: PropTypes.object.isRequired,
};

SrApplicationAssemblyLineStepLeaseTerm.stepTitle = 'Lease Term';
SrApplicationAssemblyLineStepLeaseTerm.displayName = 'SrApplicationAssemblyLineStepLeaseTerm';

SrApplicationAssemblyLineStepLeaseTerm.getStatus = ({
  assemblyLine,
  householdApplication,
}) => {
  const minLeaseTerm = assemblyLine.listing.minLeaseMonths;
  const maxLeaseTerm = assemblyLine.listing.maxLeaseMonths;
  let status = SR_APPLICATION_ASSEMBLY_LINE_STATUS_IN_PROGRESS;
  let qualifiedLeaseTerm = false;

  if (minLeaseTerm && maxLeaseTerm) {
    qualifiedLeaseTerm = householdApplication.leaseTerm >= minLeaseTerm
    && householdApplication.leaseTerm <= maxLeaseTerm;
  }
  if (minLeaseTerm && !maxLeaseTerm) {
    qualifiedLeaseTerm = householdApplication.leaseTerm >= minLeaseTerm;
  }
  if (maxLeaseTerm && !minLeaseTerm) {
    qualifiedLeaseTerm = householdApplication.leaseTerm <= maxLeaseTerm;
  }

  if (qualifiedLeaseTerm) {
    status = SR_APPLICATION_ASSEMBLY_LINE_STATUS_QUALIFIED;
  }

  return status;
};

export default SrApplicationAssemblyLineStepLeaseTerm;
